import { useAuth0 } from '@auth0/auth0-vue';
import { getUserRoles, hasTechnicianRole } from '@/utils/auth';

/**
 * Auth guard for protecting routes that require authentication and technician role
 * @param {Object} to - Route the user is navigating to
 * @param {Object} from - Route the user is navigating from
 * @param {Function} next - Function to resolve the navigation
 */
export const authGuard = async (to, from, next) => {
  console.log('🛡️ Auth Guard - Route navigation:', {
    from: from.path,
    to: to.path,
    timestamp: new Date().toISOString()
  })
  
  const auth0 = useAuth0();
  
  // Wait for Auth0 to finish loading if it's still initializing
  if (auth0.isLoading.value) {
    console.log('⏳ Auth Guard - Auth0 is loading, waiting...')
    // Wait for Auth0 to finish loading
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait
    
    while (auth0.isLoading.value && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    // If still loading after timeout, proceed anyway to avoid infinite wait
    if (auth0.isLoading.value) {
      console.warn('⚠️ Auth Guard - Auth0 loading timeout, proceeding with authentication check', {
        attempts,
        maxAttempts,
        stillLoading: auth0.isLoading.value
      });
    } else {
      console.log('✅ Auth Guard - Auth0 loading completed', {
        attempts,
        loadTime: `${attempts * 100}ms`
      })
    }
  }
  
  // Check if the user is authenticated after loading is complete
  const isAuthenticated = auth0.isAuthenticated.value;
  
  console.log('🔍 Auth Guard - Authentication check:', {
    isAuthenticated,
    isLoading: auth0.isLoading.value,
    hasUser: !!auth0.user.value?.email,
    targetRoute: to.path
  })
  
  if (isAuthenticated) {
    // Check if user has technician role
    const user = auth0.user.value;
    const roles = getUserRoles(user);
    const hasTechnicianRoleCheck = hasTechnicianRole(user);
    
    console.log('🔍 Auth Guard - Role check:', {
      userEmail: user?.email,
      roles,
      hasTechnicianRole: hasTechnicianRoleCheck,
      targetRoute: to.path
    });
    
    if (hasTechnicianRoleCheck) {
      // If authenticated and has technician role, proceed to the route
      console.log('✅ Auth Guard - User has technician role, proceeding to route:', {
        hasUser: !!user?.email,
        targetRoute: to.path
      })
      return next();
    } else {
      // If authenticated but doesn't have technician role, redirect to access denied page
      console.log('🚫 Auth Guard - User authenticated but lacks technician role:', {
        userEmail: user?.email,
        roles,
        targetRoute: to.path
      })
      
      // Redirect to access denied page instead of alert + logout
      return next('/access-denied');
    }
  } else {
    // If not authenticated and trying to access a protected route,
    // redirect to login page
    if (to.path !== '/login') {
      console.log('🚫 Auth Guard - User not authenticated, redirecting to login:', {
        attemptedRoute: to.path,
        fromRoute: from.path
      })
      
      // Store the original destination to redirect after login
      const appState = {
        targetUrl: to.fullPath
      };
      
      console.log('💾 Auth Guard - Storing redirect target:', appState)
      
      // Store the appState in localStorage to persist through the Auth0 redirect
      localStorage.setItem('authRedirectTarget', JSON.stringify(appState));
      
      // Redirect to login page
      return next('/login');
    } else {
      console.log('🔓 Auth Guard - Already on login page, proceeding')
    }
    
    // If already heading to login, allow it
    return next();
  }
};